import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { OPTION_ALL } from '../../../constants';

export const ccOptions = [
  { label: OPTION_ALL, value: '0' },
  { label: 'Capital One (4099/1974)', value: '1974' },
  { label: 'Capital One (2919/0922)', value: '0922' },
];

const transactionAdminSearchScheme = z.object({
  yearCreated: z.number(),
  dateCreated: z.number(),
  departmentID: z.number().optional(),
  costCenterID: z.number().optional(),
  accountValue: z.string(),
  removeInActiveFromDropDown: z.boolean(),
  userID: z.number(),
  statusID: z.string(),
  searchValue: z.string().optional(),
});

export type TransactionAdminSearchSchemeType = z.infer<typeof transactionAdminSearchScheme>;
export const getDefaultAdminTransactionSearchValues: ({
  acceptOverride,
}: {
  acceptOverride: boolean;
}) => TransactionAdminSearchSchemeType = ({ acceptOverride }) => ({
  yearCreated: 0,
  dateCreated: 0,
  departmentID: undefined,
  costCenterID: undefined,
  accountValue: '0',
  removeInActiveFromDropDown: true,
  userID: 0,
  statusID: acceptOverride ? '2' : '9',
  searchValue: '',
});

export const useTransactionAdminSearchForm = ({
  acceptOverride,
  disabled,
}: {
  acceptOverride: boolean;
  disabled?: boolean;
}) => {
  const defaultValues = useMemo(
    () => getDefaultAdminTransactionSearchValues({ acceptOverride }),
    [acceptOverride],
  );

  const form = useForm<TransactionAdminSearchSchemeType>({
    resolver: zodResolver(transactionAdminSearchScheme),
    mode: 'onChange',
    defaultValues,
    disabled,
  });

  const _reset = useCallback(
    (
      arg1: Parameters<(typeof form)['reset']>['0'],
      arg2: Parameters<(typeof form)['reset']>['1'],
    ) => {
      return form.reset({ ...defaultValues, statusID: '0', ...arg1 }, arg2);
    },
    [defaultValues, form],
  );

  return useMemo(() => ({ ...form, reset: _reset }), [_reset, form]);
};

export const useTransactionAdminSearchFormContext = () => {
  return useFormContext<TransactionAdminSearchSchemeType>();
};
